/**
 * Compresses an image file and resizes it to a maximum width.
 * @param {File} file - The original image file.
 * @param {number} maxWidth - Maximum width for the resized image.
 * @returns {Promise<File>} - A promise that resolves to the compressed File object.
 */
export const compressImage = (file, maxWidth = 1000, keepTransparency = false) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement("canvas");
                let width = img.width;
                let height = img.height;

                if (width > maxWidth) {
                    height = (maxWidth / width) * height;
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);

                // JPEG has no transparency (transparent areas turn black), so PNGs can stay PNG when asked
                const outType = keepTransparency && file.type === "image/png" ? "image/png" : "image/jpeg";
                canvas.toBlob((blob) => {
                    resolve(new File([blob], file.name, { type: outType }));
                }, outType, 0.85); // 85% quality
            };
        };
        reader.onerror = (e) => reject(e);
    });
};

/**
 * Specifically for logos: Allows PNG and SVG.
 * SVGs are passed through, PNGs are resized to a maximum bound for optimization
 * but kept large enough for high-DPI (Retina) screens.
 */
export const resizeLogo = (file, maxWidth = 300, maxHeight = 100) => {
    return new Promise((resolve, reject) => {
        // If it's an SVG, bypass resizing entirely to keep it vector
        if (file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg")) {
            resolve(file);
            return;
        }

        if (file.type !== "image/png" && file.type !== "image/svg+xml") {
            reject(new Error("Please upload a PNG or SVG file for the logo."));
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement("canvas");
                let width = img.width;
                let height = img.height;

                // Only resize if larger than bounds, maintaining aspect ratio
                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width = width * ratio;
                    height = height * ratio;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                
                // High quality scaling
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = "high";
                
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    resolve(new File([blob], file.name, { type: "image/png" }));
                }, "image/png");
            };
        };
        reader.onerror = (e) => reject(e);
    });
};
